// Staff home: one date range (resolved on the server in Sydney time) drives the headline numbers and
// every tab — Today (what needs doing, who's online, what just happened), Performance (sales vs
// target, reps, due to reorder, promotions, online hits) and Clients (health, money owed). Filters
// live in the URL so a refresh or a shared link shows the same view.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchDashSummary, fetchDashTodo, fetchDashActivity, fetchDashPerformance, fetchDashClients, dashExportUrl } from '../api/staffDashboard.js';
import { fetchWhoIsOnline } from '../api/presence.js';
import LiveClients from '../components/LiveClients.jsx';
import { fmtMoney } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';
import { allowedStoreCodes } from '../lib/access.js';

const PRESETS = [
  ['today', 'Today'],
  ['7d', '7 days'],
  ['month', 'This month'],
  ['30d', '30 days'],
  ['quarter', 'Quarter'],
  ['year', 'Year'],
  ['custom', 'Custom'],
];
const aud = (n) => fmtMoney(n || 0, 'AUD');
const audShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return `A$${(v / 1e6).toFixed(2)}m`;
  if (Math.abs(v) >= 1e4) return `A$${(v / 1e3).toFixed(1)}k`;
  return aud(v);
};
const errMsg = (e) => e?.response?.data?.error?.message || 'Couldn’t load this.';
const ago = (ms) => {
  const m = Math.floor(ms / 6e4);
  if (m < 60) return `${Math.max(1, m)} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
};
const sydney = (iso, opts) => new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', ...opts });
const greeting = () => {
  const h = Number(sydney(new Date().toISOString(), { hour: 'numeric', hour12: false }));
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

function Delta({ value, invert = false }) {
  if (value == null) return <span className="text-xs font-semibold text-text-muted">new</span>;
  if (value === 0) return <span className="text-xs font-semibold text-text-muted">±0%</span>;
  const good = invert ? value < 0 : value > 0;
  return <span className={`text-xs font-semibold ${good ? 'text-success' : 'text-error'}`}>{value > 0 ? '+' : ''}{value}%</span>;
}

function Kpi({ label, value, delta, invert, note, bar, barTone = 'bg-accent', title }) {
  return (
    <div className="rounded-xl border border-line bg-surface px-4 py-3.5 shadow-sm" title={title}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="text-2xl font-bold tabular-nums tracking-tight text-text-primary">{value}</span>
        {delta !== undefined && <Delta value={delta} invert={invert} />}
      </div>
      {bar != null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-raised">
          <div className={`h-full rounded-full ${barTone}`} style={{ width: `${Math.min(100, Math.max(0, bar))}%` }} />
        </div>
      )}
      {note && <div className="mt-1.5 text-xs text-text-muted">{note}</div>}
    </div>
  );
}

// --- sales chart ----------------------------------------------------------------------------------
function SalesChart({ s }) {
  const pts = s.series;
  if (!pts.length) return null;
  const W = 600;
  const H = 200;
  const pad = 28;
  const target = s.target?.amount || 0;
  const maxCum = Math.max(...pts.map((p) => p.cumulative || 0), target, 1);
  const x = (i) => pad + (i * (W - pad - 8)) / Math.max(1, pts.length - 1);
  const y = (v) => H - 22 - (v / maxCum) * (H - 40);
  const done = pts.filter((p) => p.cumulative != null);
  const line = done.map((p, i) => `${x(i)},${y(p.cumulative)}`).join(' ');
  const lastI = done.length - 1;
  const proj = s.target?.projected;
  const label = (k) => (k.length > 10 ? k.slice(11) : k.length === 7 ? new Date(`${k}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'short' }) : `${Number(k.slice(8))}/${Number(k.slice(5, 7))}`);
  return (
    <svg role="img" aria-label={`Sales so far ${aud(s.sales.value)}${target ? ` against a target of ${aud(target)}` : ''}`} viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full">
      <line x1={pad} y1={H - 22} x2={W - 8} y2={H - 22} stroke="#E3E7EE" />
      <text x={0} y={y(maxCum) + 4} fontSize="11" fill="#64748B">{audShort(maxCum)}</text>
      {target > 0 && (
        <>
          <line x1={pad} y1={y(0)} x2={x(pts.length - 1)} y2={y(target)} stroke="#94A3B8" strokeWidth="2" strokeDasharray="6 5" />
          <text x={W - 8} y={y(target) - 6} fontSize="11" fill="#64748B" textAnchor="end">Target {audShort(target)}</text>
        </>
      )}
      {proj && lastI >= 0 && lastI < pts.length - 1 && <line x1={x(lastI)} y1={y(done[lastI].cumulative)} x2={x(pts.length - 1)} y2={y(Math.min(proj, maxCum))} stroke="#111827" strokeWidth="2" strokeDasharray="3 5" opacity=".45" />}
      {done.length > 1 && <polyline points={line} fill="none" stroke="#111827" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}
      {lastI >= 0 && <circle cx={x(lastI)} cy={y(done[lastI].cumulative)} r="4.5" fill="#111827" />}
      <text x={pad} y={H - 6} fontSize="11" fill="#64748B">{label(pts[0].key)}</text>
      <text x={W - 8} y={H - 6} fontSize="11" fill="#64748B" textAnchor="end">{label(pts[pts.length - 1].key)}</text>
    </svg>
  );
}

// --- tabs -----------------------------------------------------------------------------------------
const TONE = { warn: 'text-warning', info: 'text-info', error: 'text-error', purple: 'text-violet-700' };
const DOT = { warn: 'bg-warning', info: 'bg-info', error: 'bg-error', purple: 'bg-violet-600' };

function TodayTab({ todo, activity, online }) {
  return (
    <div className="flex flex-wrap items-start gap-5">
      <section aria-label="To do" className="min-w-0 flex-[2_1_600px] overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
        <div className="flex items-baseline justify-between px-5 py-4">
          <h2 className="text-lg font-bold text-text-primary">To do</h2>
          <span className="text-sm text-text-muted">{todo ? `${todo.total} item${todo.total === 1 ? '' : 's'}` : 'Loading…'}</span>
        </div>
        {todo?.total === 0 && <p className="border-t border-line px-5 py-8 text-center text-sm text-text-muted">Nothing waiting on you. Nice.</p>}
        {todo?.groups.filter((g) => g.items.length).map((g) => (
          <div key={g.key} className="border-t border-line">
            <div className={`flex items-center gap-2 bg-surface-raised/50 px-5 py-2 text-xs font-bold uppercase tracking-wide ${TONE[g.tone]}`}>
              <span className={`h-2 w-2 rounded-full ${DOT[g.tone]}`} />
              {g.title} <span className="font-semibold text-text-muted">· {g.items.length}</span>
            </div>
            <ul>
              {g.items.slice(0, 8).map((i) => (
                <li key={i.id} className="flex flex-wrap items-center gap-3 border-t border-surface-raised px-5 py-3">
                  <div className="min-w-0 flex-[1_1_260px]">
                    <div className="text-sm font-semibold text-text-primary">{i.title}</div>
                    <div className="mt-0.5 text-xs text-text-muted">{i.sub}</div>
                  </div>
                  <span className={`text-xs font-semibold ${i.ageMs > 3 * 864e5 ? 'text-error' : i.ageMs > 864e5 ? 'text-warning' : 'text-text-muted'}`}>{ago(i.ageMs)}</span>
                  <Link to={i.href} className="whitespace-nowrap rounded-lg bg-accent px-3 py-1.5 text-[13px] font-bold text-white hover:bg-accent-hover">{i.action}</Link>
                </li>
              ))}
              {g.items.length > 8 && <li className="border-t border-surface-raised px-5 py-2 text-xs text-text-muted">+ {g.items.length - 8} more</li>}
            </ul>
          </div>
        ))}
      </section>
      <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-5">
        <LiveClients users={online} />
        <section aria-label="What just happened" className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <h2 className="text-[15px] font-bold text-text-primary">What just happened</h2>
          {activity?.items.length === 0 && <p className="mt-2 text-sm text-text-muted">Nothing in the last two weeks.</p>}
          <ul className="mt-1">
            {(activity?.items || []).map((a, i) => (
              <li key={i} className="flex gap-2.5 py-1.5 text-[13px]">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.kind === 'payment' ? 'bg-success' : a.kind === 'email' ? 'bg-info' : a.kind === 'order-off' ? 'bg-error' : 'bg-accent'}`} />
                <Link to={a.href} className="min-w-0 flex-1 hover:underline">
                  <span className="font-semibold text-text-primary">{a.who}</span> <span className="text-text-secondary">{a.what}</span>
                </Link>
                <span className="shrink-0 text-xs text-text-muted">{ago(Date.now() - new Date(a.at).getTime())}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function PerformanceTab({ s, p }) {
  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Sales" className="flex flex-wrap gap-6 rounded-2xl border border-line bg-surface p-5 shadow-sm">
        <div className="flex-[1_1_240px]">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Sales · {s.range.label}</div>
          <div className="mt-1 text-3xl font-extrabold tabular-nums tracking-tight text-text-primary">{aud(s.sales.value)}</div>
          <div className="mt-1 text-sm">
            <Delta value={s.sales.delta} /> <span className="text-text-muted">vs {s.range.compare} ({aud(s.sales.prev)})</span>
          </div>
          {s.target && (
            <div className="mt-2 text-sm font-semibold text-text-primary">
              {s.target.pctOfTarget}% of the {aud(s.target.amount)} target
              {s.target.projected != null && (
                <span className={`block font-normal ${s.target.projected >= s.target.amount ? 'text-success' : 'text-warning'}`}>
                  On pace for {audShort(s.target.projected)} ({Math.round((s.target.projected / s.target.amount) * 100)}% of target)
                </span>
              )}
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2.5">
            <div className="rounded-lg border border-line p-2.5"><div className="text-xs text-text-muted">Australia</div><div className="font-bold tabular-nums">{audShort(s.sales.byStore.A)}</div></div>
            <div className="rounded-lg border border-line p-2.5"><div className="text-xs text-text-muted">International</div><div className="font-bold tabular-nums">{audShort(s.sales.byStore.B)}</div></div>
          </div>
          {s.sales.pending > 0 && <p className="mt-2 text-xs text-text-muted">Includes {aud(s.sales.pending)} awaiting approval.</p>}
        </div>
        <figure className="m-0 flex-[2_1_480px]">
          <figcaption className="mb-2 text-[13px] font-semibold text-text-secondary">{s.target ? 'Sales so far vs target' : 'Sales over the range (running total)'}</figcaption>
          <SalesChart s={s} />
        </figure>
      </section>

      {!p ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-start gap-5">
            <section aria-label="Due to reorder" className="min-w-0 flex-[3_1_560px] overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
              <div className="px-5 py-4">
                <h2 className="text-lg font-bold text-text-primary">Due to reorder</h2>
                <p className="mt-0.5 text-[13px] text-text-muted">
                  Past their usual gap between orders{p.due.length ? ` · about ${audShort(p.dueValue)} if they all order their typical amount` : ''}
                </p>
              </div>
              {p.due.length === 0 ? (
                <p className="border-t border-line px-5 py-6 text-sm text-text-muted">No one is overdue for their next order. (Clients need three orders before we know their rhythm.)</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-[13px]">
                    <thead className="bg-surface-raised/60 text-left text-xs text-text-muted">
                      <tr><th className="px-5 py-2 font-semibold">Client</th><th className="px-3 py-2 font-semibold">Usually every</th><th className="px-3 py-2 font-semibold">Last order</th><th className="px-5 py-2 text-right font-semibold">Typical order</th></tr>
                    </thead>
                    <tbody>
                      {p.due.map((d) => (
                        <tr key={d.id} className="border-t border-surface-raised">
                          <td className="px-5 py-2.5"><Link to={`/clients/${d.id}`} className="font-semibold text-text-primary hover:underline">{d.name}</Link><div className="text-xs text-text-muted">{d.rep} · {d.store === 'A' ? 'AU' : 'Intl'}</div></td>
                          <td className="px-3 py-2.5">{Math.round(d.every / 7)} weeks</td>
                          <td className="px-3 py-2.5 font-semibold text-warning">{Math.round(d.daysSince / 7)} weeks ago</td>
                          <td className="px-5 py-2.5 text-right font-semibold tabular-nums">{aud(d.typical)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section aria-label="Reps" className="min-w-0 flex-[2_1_340px] rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <h2 className="text-lg font-bold text-text-primary">Reps · {s.range.label}</h2>
              {p.reps.length === 0 && <p className="mt-2 text-sm text-text-muted">No sales in this range yet.</p>}
              {p.reps.map((r) => (
                <div key={r.id || 'house'} className="border-t border-surface-raised py-2.5 first-of-type:mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold text-text-primary">{r.name}</span>
                    <span className="tabular-nums"><strong>{aud(r.sales)}</strong>{r.target ? <span className="text-xs text-text-muted"> / {audShort(r.target)}</span> : null}</span>
                  </div>
                  {r.target ? <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-raised"><div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(100, (r.sales / r.target) * 100)}%` }} /></div> : null}
                  <div className="mt-1 text-xs text-text-muted">{r.orders} order{r.orders === 1 ? '' : 's'} · {r.clients} client{r.clients === 1 ? '' : 's'}</div>
                </div>
              ))}
            </section>
          </div>
          <div className="flex flex-wrap items-stretch gap-5">
            {p.promos.map((pr) => (
              <section key={pr.id} aria-label={pr.name} className="min-w-0 flex-[1_1_300px] rounded-2xl border border-line bg-surface p-5 shadow-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="text-[15px] font-bold text-text-primary">{pr.name}</h2>
                  {pr.endsAt && <span className="shrink-0 text-xs font-semibold text-orange-700">{Math.max(0, Math.ceil((new Date(pr.endsAt) - Date.now()) / 864e5))} days left</span>}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div><div className="text-lg font-bold tabular-nums">{audShort(pr.sales)}</div><div className="text-xs text-text-muted">sales</div></div>
                  <div><div className="text-lg font-bold tabular-nums">{pr.clients} / {pr.targeted}</div><div className="text-xs text-text-muted">clients ordered</div></div>
                  <div><div className="text-lg font-bold tabular-nums">{audShort(pr.discount)}</div><div className="text-xs text-text-muted">discount given</div></div>
                </div>
                <Link to="/discounts" className="mt-3 inline-block text-[13px] font-semibold text-text-primary hover:underline">Results &amp; reminders</Link>
              </section>
            ))}
            <section aria-label="Online hits your stockists don't carry" className="min-w-0 flex-[2_1_480px] rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <h2 className="text-lg font-bold text-text-primary">Online hits your stockists don’t carry</h2>
              <p className="mt-0.5 text-[13px] text-text-muted">Top sellers in your online stores that most wholesale clients in that market haven’t ordered (last 12 months)</p>
              {p.onlineGaps.length === 0 && <p className="mt-3 text-sm text-text-muted">Nothing stands out — your stockists already carry the online best sellers.</p>}
              <ul className="mt-2">
                {p.onlineGaps.map((g) => (
                  <li key={`${g.store}-${g.productId}-${g.market}`} className="flex items-center gap-3 border-t border-surface-raised py-2.5">
                    {g.image ? <img src={g.image} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" /> : <div className="h-11 w-11 shrink-0 rounded-lg bg-surface-raised" />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-text-primary">{g.title}</div>
                      <div className="text-xs text-text-muted">#{g.rank} online in {g.market} · {g.carry} of {g.stockists} stockists carry it</div>
                    </div>
                    <Link to="/marketing" className="shrink-0 rounded-lg border border-line-strong px-2.5 py-1.5 text-xs font-bold text-text-primary hover:bg-surface-raised">Email them</Link>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

const SEG = {
  growing: ['Growing', 'Ordering more than the half before', 'bg-success', 'text-success bg-success-light'],
  steady: ['Steady', 'On their usual rhythm', 'bg-info', 'text-info bg-info-light'],
  slipping: ['Slipping', 'Late vs their usual gap', 'bg-warning', 'text-warning bg-warning-light'],
  lapsed: ['Lapsed', 'No order in 6+ months', 'bg-error', 'text-error bg-error-light'],
  new: ['New', 'First order in the last 90 days', 'bg-violet-600', 'text-violet-700 bg-violet-50'],
  never: ['Never ordered', 'Have a login, no order yet', 'bg-line-strong', 'text-text-secondary bg-surface-raised'],
};

function Spark({ values, tone }) {
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => `${(i * 110) / (values.length - 1)},${26 - (v / max) * 22}`).join(' ');
  return (
    <svg aria-hidden="true" width="110" height="28" viewBox="0 0 110 28">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" className={tone} />
    </svg>
  );
}

function ClientsTab({ c, rangeLabel }) {
  const [seg, setSeg] = useState('');
  if (!c) return <div className="text-sm text-text-muted">Loading…</div>;
  const rows = c.rows.filter((r) => !seg || r.segment === seg);
  const quad = [
    ['champions', 'Champions', 'High spend · ordered recently', 'bg-success-light', 'Keep close: early access to new drops.'],
    ['atRisk', 'At risk', 'High spend · gone quiet', 'bg-error-light', 'Call this week.'],
    ['grow', 'Grow', 'Lower spend · ordering', 'bg-info-light', 'Send best sellers and range gaps.'],
    ['dormant', 'Dormant', 'Lower spend · gone quiet', 'bg-surface-raised', 'A win-back email.'],
  ];
  const ag = c.money?.aging || {};
  const agTotal = Object.values(ag).reduce((s, v) => s + v, 0) || 1;
  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Segments" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {c.segments.map((s) => (
          <button key={s.key} type="button" onClick={() => setSeg(seg === s.key ? '' : s.key)} aria-pressed={seg === s.key} className={`flex flex-col gap-1 rounded-xl border bg-surface p-3.5 text-left shadow-sm ${seg === s.key ? 'border-accent ring-2 ring-accent/20' : 'border-line hover:border-line-strong'}`}>
            <span className="flex items-center gap-2 text-[13px] font-semibold text-text-secondary"><span className={`h-2.5 w-2.5 rounded-full ${SEG[s.key][2]}`} />{SEG[s.key][0]}</span>
            <span className="text-2xl font-bold tabular-nums text-text-primary">{s.count}</span>
            <span className="text-xs text-text-muted">{SEG[s.key][1]}</span>
          </button>
        ))}
      </section>
      <div className="flex flex-wrap items-stretch gap-5">
        <section aria-label="Who matters" className="min-w-0 flex-[3_1_560px] rounded-2xl border border-line bg-surface p-5 shadow-sm">
          <h2 className="text-lg font-bold text-text-primary">Who matters, and who’s drifting</h2>
          <p className="mb-3 mt-0.5 text-[13px] text-text-muted">Top 30% by spend over the last 12 months, against ordering in the last 60 days</p>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {quad.map(([k, title, sub, bg, act]) => (
              <div key={k} className={`rounded-xl p-3 ${bg}`}>
                <div className="text-sm font-bold text-text-primary">{title} <span className="text-xs font-normal text-text-secondary">· {sub}</span></div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.quadrants[k].length ? c.quadrants[k].map((x) => <Link key={x.id} to={`/clients/${x.id}`} className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-xs font-semibold text-text-primary hover:border-accent">{x.name}</Link>) : <span className="text-xs text-text-muted">No one</span>}
                </div>
                <div className="mt-2 text-xs text-text-secondary">{act}</div>
              </div>
            ))}
          </div>
        </section>
        {c.money && (
          <section aria-label="Money owed" className="flex min-w-0 flex-[2_1_320px] flex-col gap-3 rounded-2xl border border-line bg-surface p-5 shadow-sm">
            <div>
              <h2 className="text-lg font-bold text-text-primary">Money owed</h2>
              <div className="mt-1 text-2xl font-bold tabular-nums">{aud(c.money.outstanding)}</div>
              <div className="text-[13px] text-text-muted">{c.money.clients} client{c.money.clients === 1 ? '' : 's'} · <span className="font-semibold text-error">{aud(c.money.overdue)} overdue</span></div>
            </div>
            <div aria-hidden="true" className="flex h-3 overflow-hidden rounded-full bg-surface-raised">
              <span className="bg-slate-400" style={{ width: `${(ag.current / agTotal) * 100}%` }} />
              <span className="bg-amber-500" style={{ width: `${(ag.d1_30 / agTotal) * 100}%` }} />
              <span className="bg-orange-600" style={{ width: `${(ag.d31_60 / agTotal) * 100}%` }} />
              <span className="bg-red-700" style={{ width: `${((ag.d61_90 + ag.d90plus) / agTotal) * 100}%` }} />
            </div>
            <div className="grid grid-cols-4 gap-1.5 text-xs text-text-secondary">
              <span>Not due<br /><strong className="tabular-nums text-text-primary">{audShort(ag.current)}</strong></span>
              <span>1–30 d<br /><strong className="tabular-nums text-text-primary">{audShort(ag.d1_30)}</strong></span>
              <span>31–60 d<br /><strong className="tabular-nums text-text-primary">{audShort(ag.d31_60)}</strong></span>
              <span>60+ d<br /><strong className="tabular-nums text-text-primary">{audShort(ag.d61_90 + ag.d90plus)}</strong></span>
            </div>
            <Link to="/receivables" className="text-[13px] font-semibold text-text-primary hover:underline">Open receivables</Link>
          </section>
        )}
      </div>
      <section aria-label="Clients" className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-4">
          <h2 className="text-lg font-bold text-text-primary">{seg ? `${SEG[seg][0]} clients` : 'Your book'} <span className="text-sm font-medium text-text-muted">· {rows.length}</span></h2>
          {seg && <button type="button" onClick={() => setSeg('')} className="text-[13px] font-semibold text-text-primary hover:underline">Show everyone</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-[13px]">
            <thead className="bg-surface-raised/60 text-left text-xs text-text-muted">
              <tr>
                <th className="px-5 py-2 font-semibold">Client</th><th className="px-3 py-2 font-semibold">Health</th><th className="px-3 py-2 font-semibold">Last 6 months</th><th className="px-3 py-2 font-semibold">Last order</th>
                <th className="px-3 py-2 text-right font-semibold">Spend · {rangeLabel}</th><th className="px-3 py-2 text-right font-semibold">12 months</th><th className="px-5 py-2 text-right font-semibold">Owed</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r) => (
                <tr key={r.id} className="border-t border-surface-raised">
                  <td className="px-5 py-2.5"><Link to={`/clients/${r.id}`} className="font-semibold text-text-primary hover:underline">{r.name}</Link><div className="text-xs text-text-muted">{r.store === 'A' ? 'AU' : 'Intl'} · {r.rep}</div></td>
                  <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${SEG[r.segment]?.[3] || ''}`}>{SEG[r.segment]?.[0] || r.segment}</span></td>
                  <td className="px-3 py-2.5"><Spark values={r.trend} tone={(SEG[r.segment]?.[3] || 'text-text-muted').split(' ')[0]} /></td>
                  <td className="px-3 py-2.5 text-text-secondary">{r.daysSince == null ? '—' : r.daysSince === 0 ? 'Today' : r.daysSince < 14 ? `${r.daysSince} days ago` : `${Math.round(r.daysSince / 7)} weeks ago`}</td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{r.spend ? aud(r.spend) : '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-text-secondary">{r.year ? audShort(r.year) : '—'}</td>
                  <td className={`px-5 py-2.5 text-right font-semibold tabular-nums ${r.overdue > 0 ? 'text-error' : 'text-text-secondary'}`}>{r.owed ? aud(r.owed) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// --- page -----------------------------------------------------------------------------------------
export default function StaffHome() {
  const user = useAuthStore((s) => s.user);
  const [params, setParams] = useSearchParams();
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
  const f = {
    range: params.get('range') || 'month',
    from: params.get('from') || today,
    to: params.get('to') || today,
    store: params.get('store') || '',
    mine: params.get('mine') === '1',
  };
  const tab = params.get('tab') || 'today';
  const set = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === '' || v == null || v === false) next.delete(k);
      else next.set(k, v === true ? '1' : v);
    }
    setParams(next, { replace: true });
  };
  const stores = allowedStoreCodes(user) || ['A', 'B'];
  const customBad = f.range === 'custom' && (!f.from || !f.to || f.from > f.to);
  const key = JSON.stringify(f);

  const [summary, setSummary] = useState(null);
  const [todo, setTodo] = useState(null);
  const [activity, setActivity] = useState(null);
  const [online, setOnline] = useState(null);
  const [perf, setPerf] = useState(null);
  const [clients, setClients] = useState(null);
  const [error, setError] = useState('');

  // Range-driven data. Each response is checked against the request it answers, so a slow older
  // response can never overwrite a newer range's numbers.
  useEffect(() => {
    if (customBad) return undefined;
    let alive = true;
    setError('');
    setSummary((s) => (s ? { ...s, stale: true } : s));
    fetchDashSummary(f).then((d) => alive && setSummary(d)).catch((e) => alive && setError(errMsg(e)));
    if (tab === 'performance') {
      setPerf(null);
      fetchDashPerformance(f).then((d) => alive && setPerf(d)).catch((e) => alive && setError(errMsg(e)));
    }
    if (tab === 'clients') {
      setClients(null);
      fetchDashClients(f).then((d) => alive && setClients(d)).catch((e) => alive && setError(errMsg(e)));
    }
    return () => {
      alive = false;
    };
  }, [key, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Today: the to-do list and activity follow store / "my clients" (not the date range).
  useEffect(() => {
    let alive = true;
    fetchDashTodo(f).then((d) => alive && setTodo(d)).catch(() => alive && setTodo({ total: 0, groups: [] }));
    fetchDashActivity(f).then((d) => alive && setActivity(d)).catch(() => alive && setActivity({ items: [] }));
    return () => {
      alive = false;
    };
  }, [f.store, f.mine]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    const load = () => fetchWhoIsOnline().then((u) => alive && setOnline(u)).catch(() => alive && setOnline([]));
    load();
    const t = setInterval(load, 30e3);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const s = summary;
  const r = s?.range;
  const exactRange = useMemo(() => {
    if (!r) return '';
    const a = sydney(r.from, { day: 'numeric', month: 'short', year: 'numeric' });
    const b = sydney(new Date(new Date(r.to).getTime() - 1).toISOString(), { day: 'numeric', month: 'short', year: 'numeric', ...(r.preset === 'custom' ? {} : { hour: 'numeric', minute: '2-digit' }) });
    return `${a} – ${b} (Sydney time)`;
  }, [r]);

  return (
    <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[13px] text-text-muted">{sydney(new Date().toISOString(), { weekday: 'long', day: 'numeric', month: 'long' })}</div>
          <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-text-primary sm:text-[28px]">
            {greeting()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
            {todo?.total ? <span className="font-normal text-text-secondary"> — {todo.total} thing{todo.total === 1 ? '' : 's'} need{todo.total === 1 ? 's' : ''} you</span> : null}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Whose clients" className="inline-flex rounded-lg bg-surface-raised p-0.5">
            {[[false, 'Everything'], [true, 'My clients']].map(([v, l]) => (
              <button key={l} type="button" onClick={() => set({ mine: v })} aria-pressed={f.mine === v} className={`rounded-md px-3 py-1.5 text-[13px] ${f.mine === v ? 'bg-surface font-semibold text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'}`}>{l}</button>
            ))}
          </div>
          {stores.length > 1 && (
            <div role="group" aria-label="Store" className="inline-flex rounded-lg bg-surface-raised p-0.5">
              {[['', 'Both stores'], ['A', 'Australia'], ['B', 'International']].map(([v, l]) => (
                <button key={l} type="button" onClick={() => set({ store: v })} aria-pressed={f.store === v} className={`rounded-md px-3 py-1.5 text-[13px] ${f.store === v ? 'bg-surface font-semibold text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'}`}>{l}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Date range" className="inline-flex flex-wrap rounded-lg bg-surface-raised p-0.5">
          {PRESETS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => set({ range: k === 'month' ? '' : k, ...(k === 'custom' ? { from: f.from, to: f.to } : { from: '', to: '' }) })} aria-pressed={f.range === k} className={`rounded-md px-3 py-1.5 text-[13px] ${f.range === k ? 'bg-surface font-semibold text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'}`}>
              {l}
            </button>
          ))}
        </div>
        {f.range === 'custom' && (
          <span className="flex items-center gap-1.5 text-[13px]">
            <label className="sr-only" htmlFor="dash-from">From</label>
            <input id="dash-from" type="date" value={f.from} max={f.to || undefined} onChange={(e) => set({ from: e.target.value })} className="rounded-md border border-line bg-surface px-2 py-1" />
            <span className="text-text-muted">to</span>
            <label className="sr-only" htmlFor="dash-to">To</label>
            <input id="dash-to" type="date" value={f.to} min={f.from || undefined} onChange={(e) => set({ to: e.target.value })} className="rounded-md border border-line bg-surface px-2 py-1" />
          </span>
        )}
        <span className="text-xs text-text-muted">{customBad ? <span className="text-error">The end date must be on or after the start date.</span> : r ? <>{exactRange} · compared with {r.compare}</> : ''}</span>
        <a href={customBad ? undefined : dashExportUrl(f)} className="ml-auto rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] font-semibold text-text-secondary hover:bg-surface-raised">⤓ Export</a>
      </div>

      {error && <p className="rounded-md bg-error-light px-3 py-2 text-sm text-error">{error}</p>}

      {/* Headline numbers */}
      <section aria-label="Headline numbers" className={`grid grid-cols-2 gap-3 transition-opacity lg:grid-cols-5 ${s?.stale ? 'opacity-50' : ''}`}>
        {s ? (
          <>
            <Kpi
              label={`Sales · ${r.label}`}
              value={audShort(s.sales.value)}
              delta={s.sales.delta}
              bar={s.target ? s.target.pctOfTarget : null}
              note={s.target ? `${s.target.pctOfTarget}% of ${audShort(s.target.amount)} target${s.target.projected ? ` · on pace for ${audShort(s.target.projected)}` : ''}` : `vs ${audShort(s.sales.prev)} ${r.compare}`}
              title="Goods after discounts, before shipping, of orders placed in the range (approved or awaiting approval). In AUD at today's rates."
            />
            <Kpi label="Orders" value={s.orders.value} delta={s.orders.delta} note={`${s.orders.clients} client${s.orders.clients === 1 ? '' : 's'} · avg ${audShort(s.orders.aov)}`} />
            <Kpi label="Cash collected" value={audShort(s.cash.value)} delta={s.cash.delta} barTone="bg-success" note="Payments received in the range" />
            <Kpi label="Overdue now" value={audShort(s.overdue.value)} note={`${s.overdue.invoices} payment${s.overdue.invoices === 1 ? '' : 's'} · ${s.overdue.clients} client${s.overdue.clients === 1 ? '' : 's'}`} title="Everything overdue today, whatever the date range." />
            <Kpi label="Clients online" value={online?.length ?? '…'} note={`${s.visitors} visited in the range`} />
          </>
        ) : (
          Array.from({ length: 5 }, (_, i) => <div key={i} className="h-[104px] animate-pulse rounded-xl border border-line bg-surface" />)
        )}
      </section>

      {/* Tabs */}
      <div role="tablist" aria-label="Dashboard views" className="flex gap-1 border-b border-line">
        {[['today', 'Today', todo?.total], ['performance', 'Performance'], ['clients', 'Clients']].map(([k, l, n]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => set({ tab: k === 'today' ? '' : k })} className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-[15px] font-semibold ${tab === k ? 'border-accent text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}>
            {l}
            {n ? <span className="rounded-full bg-orange-700 px-1.5 text-[11px] font-bold text-white">{n}</span> : null}
          </button>
        ))}
      </div>

      {tab === 'today' && <TodayTab todo={todo} activity={activity} online={online} />}
      {tab === 'performance' && (s ? <PerformanceTab s={s} p={perf} /> : <div className="text-sm text-text-muted">Loading…</div>)}
      {tab === 'clients' && <ClientsTab c={clients} rangeLabel={r?.label || ''} />}
    </div>
  );
}
